import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner, Nav, Tab, Col } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall, getWithAuthCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import {
  toDateInput,
  todayInput,
  useVehicles,
  VehicleSelect,
  vehicleNameById,
  labelFor,
  formatValue,
  isPositiveNumber,
} from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const emptyConfig = {
  vehicle_id: "",
  acquisition_cost: "",
  residual_value: "",
  in_service_date: "",
  useful_life_years: "",
  planned_cpk: "",
  expected_km: "",
  odometer_start: "",
  odometer_current: "",
};

// value formatter so any response shape renders cleanly
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "number") return v.toLocaleString();
  if (!isNaN(v)) return Number(v).toLocaleString();
  return String(v);
};
// pull a value out of a row trying several possible key names
const pick = (obj, keys, fallback = "-") => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
      return fmtVal(obj[k]);
  }
  return fallback;
};

// U-03: labels via labelFor ("Tco" -> "Total Cost of Ownership"), values via formatValue
const isNoteKey = (k) => /basis|formula|note|method|description/i.test(k);
const ResultCard = ({ title, data, vehicles }) => (
  <div className="col-md-6 mb-3">
    <div
      style={{
        border: "0.5px solid #f6efe9",
        borderRadius: 12,
        padding: 16,
      }}
    >
      <p style={{ color: "#8f4300", fontSize: 16, marginBottom: 12 }}>{title}</p>
      <table className="table" style={{ marginBottom: 0 }}>
        <tbody>
          {Object.entries(data)
            .filter(([k]) => !isNoteKey(k))
            .map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: "#703c19", border: "none", padding: "6px 0" }}>
                {labelFor(k)}
              </td>
              <td
                style={{
                  border: "none",
                  padding: "6px 0",
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {formatValue(v, k, vehicles)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* U-03: basis / formula strings read as a footnote */}
      {Object.entries(data)
        .filter(([k, v]) => isNoteKey(k) && v)
        .map(([k, v]) => (
          <p key={k} style={{ fontSize: 12, opacity: 0.7, marginTop: 10, marginBottom: 0 }}>
            {labelFor(k)}: {String(v)}
          </p>
        ))}
    </div>
  </div>
);

const FleetRow = ({ r, i, vehicles }) => (
  <tr key={r.vehicle_id || i}>
    {/* U-02: resolve the vehicle name when the row only carries the id */}
    <td>{r.vehicle_name || r.vehicle_number || vehicleNameById(vehicles, r.vehicle_id)}</td>
    <td>{pick(r, ["acquisition_cost", "acquisition"])}</td>
    <td>{pick(r, ["operating_cost", "operating", "operating_total"])}</td>
    <td>{pick(r, ["depreciation", "cumulative_depreciation", "depreciation_total"])}</td>
    <td>{pick(r, ["residual_value", "residual"])}</td>
    <td>{pick(r, ["tco", "ownership_cost", "total_cost", "total_tco"])}</td>
    <td>{pick(r, ["cpk", "cost_per_km", "actual_cpk"])}</td>
  </tr>
);

const TcoCpk = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [config, setConfig] = useState(emptyConfig);
  const [saving, setSaving] = useState(false);
  const [tco, setTco] = useState(null);
  const [cpk, setCpk] = useState(null);
  const [computing, setComputing] = useState(false);

  const [fleet, setFleet] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const vehicles = useVehicles(); // U-01

  // JSON report (fleet-finance/tco/report)
  const [showReport, setShowReport] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    getFleetSummary();
  }, []);

  const changeConfig = (e) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  const loadConfig = () => {
    if (!config.vehicle_id) {
      notifyError("Select a vehicle first");
      return;
    }
    getWithAuthCall(ApiConfig.FLEET_FINANCE_TCO_CONFIG_SINGLE + config.vehicle_id)
      .then((data) => {
        const d = data?.data;
        if ((data?.success || data?.result) && d) {
          setConfig({
            vehicle_id: d.vehicle_id ?? config.vehicle_id,
            acquisition_cost: d.acquisition_cost ?? "",
            residual_value: d.residual_value ?? "",
            in_service_date: toDateInput(d.in_service_date), // FF-06
            useful_life_years: d.useful_life_years ?? "",
            planned_cpk: d.planned_cpk ?? "",
            expected_km: d.expected_km ?? "",
            odometer_start: d.odometer_start ?? "",
            odometer_current: d.odometer_current ?? "",
          });
          notifySuccess("Config loaded");
        } else {
          notifyError(data?.message || "No config found for this vehicle");
        }
      })
      .catch((err) => {
        console.log("load config err", err);
        notifyError("Something went wrong");
      });
  };

  const saveConfig = (e) => {
    e.preventDefault();
    // FF-11: sanity checks before saving
    if (!config.vehicle_id) return notifyError(t("Select a vehicle first"));
    if (!isPositiveNumber(config.acquisition_cost))
      return notifyError(t("Acquisition cost must be greater than 0"));
    if (!isPositiveNumber(config.useful_life_years))
      return notifyError(t("Useful life (years) must be greater than 0"));
    if (Number(config.residual_value) < 0)
      return notifyError(t("Residual value cannot be negative"));
    if (Number(config.residual_value) > Number(config.acquisition_cost))
      return notifyError(t("Residual value cannot exceed acquisition cost"));
    if (
      config.odometer_start !== "" &&
      config.odometer_current !== "" &&
      Number(config.odometer_current) < Number(config.odometer_start)
    )
      return notifyError(t("Odometer current cannot be less than odometer start"));
    setSaving(true);
    const body = JSON.stringify({
      vehicle_id: Number(config.vehicle_id),
      acquisition_cost: Number(config.acquisition_cost) || 0,
      residual_value: Number(config.residual_value) || 0,
      in_service_date: config.in_service_date,
      useful_life_years: Number(config.useful_life_years) || 0,
      planned_cpk: Number(config.planned_cpk) || 0,
      expected_km: Number(config.expected_km) || 0,
      odometer_start: Number(config.odometer_start) || 0,
      odometer_current: Number(config.odometer_current) || 0,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_CONFIG_SAVE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "TCO config saved");
        } else {
          notifyError(data?.message || "Unable to save config");
        }
      })
      .catch((err) => {
        console.log("save config err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  const computeTco = () => {
    if (!config.vehicle_id) return notifyError("Select a vehicle first");
    setComputing(true);
    setTco(null);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_TCO_COMPUTE,
      JSON.stringify({ vehicle_id: Number(config.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) setTco(data?.data || {});
        else notifyError(data?.message || "Unable to compute TCO");
      })
      .catch((err) => {
        console.log("compute tco err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setComputing(false));
  };

  const computeCpk = () => {
    if (!config.vehicle_id) return notifyError("Select a vehicle first");
    setCpk(null);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_TCO_CPK,
      JSON.stringify({ vehicle_id: Number(config.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) setCpk(data?.data || {});
        else notifyError(data?.message || "Unable to compute CPK");
      })
      .catch((err) => {
        console.log("compute cpk err", err);
        notifyError("Something went wrong");
      });
  };

  const getFleetSummary = () => {
    setFleetLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_FLEET_SUMMARY, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data;
          setFleet(Array.isArray(d) ? d : d?.rows || d?.vehicles || []);
        } else {
          setFleet([]);
        }
      })
      .catch((err) => {
        console.log("fleet summary err", err);
        setFleet([]);
      })
      .finally(() => setFleetLoading(false));
  };

  const viewReport = () => {
    setShowReport(true);
    setReportLoading(true);
    setReport(null);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_REPORT, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result)
          setReport(data?.report || data?.data || data);
        else notifyError(data?.message || "Unable to load report");
      })
      .catch((err) => {
        console.log("tco report err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setReportLoading(false));
  };

  const downloadPdf = () => {
    setPdfLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_REPORT_PDF, JSON.stringify({}))
      .then((data) => {
        const filepath = data?.data?.filepath || data?.filepath;
        if ((data?.success || data?.result) && filepath) {
          window.open(ApiConfig.BASE_URL + filepath, "_blank");
        } else {
          notifyError(data?.message || "Unable to generate report");
        }
      })
      .catch((err) => {
        console.log("report pdf err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setPdfLoading(false));
  };

  const reportRows = report?.rows || report?.vehicles || [];

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_TCO">
        <div className="main-master-wrapper">
        <Tab.Container defaultActiveKey="config">
          <Nav variant="pills" className="td-nav" id="FleetFinanceTcoTabs">
            <Nav.Item className="td-tab">
              <Nav.Link className="td-link" eventKey="config">
                {t("TCO Configuration")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="td-tab">
              <Nav.Link className="td-link" eventKey="fleet">
                {t("Fleet Summary")}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Col sm={12} className="mt-3">
            <Tab.Content>
              {/* ===================== TAB 1: TCO Configuration ===================== */}
              <Tab.Pane eventKey="config">
                <div>
                  <div className="vehicleHead">
                    <p>{t("Vehicle TCO Configuration")}</p>
                  </div>
                  <Form onSubmit={saveConfig}>
                    <div className="row">
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Vehicle")} <span>&#42;</span>
                        </Form.Label>
                        <div className="d-flex">
                          {/* U-01 */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <VehicleSelect
                              vehicles={vehicles}
                              name="vehicle_id"
                              value={config.vehicle_id}
                              onChange={changeConfig}
                            />
                          </div>
                          <button
                            type="button"
                            className="cx-btn-1"
                            style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                            onClick={loadConfig}
                          >
                            {t("Load")}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Acquisition Cost")}
                        </Form.Label>
                        {/* FF-11: min attributes */}
                        <Form.Control
                          type="number"
                          min="0"
                          name="acquisition_cost"
                          value={config.acquisition_cost}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Residual Value")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max={config.acquisition_cost || undefined}
                          name="residual_value"
                          value={config.residual_value}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("In-Service Date")}
                        </Form.Label>
                        <Form.Control
                          type="date"
                          max={todayInput()}
                          name="in_service_date"
                          value={config.in_service_date}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Useful Life (years)")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          name="useful_life_years"
                          value={config.useful_life_years}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Planned CPK")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          name="planned_cpk"
                          value={config.planned_cpk}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Expected KM")}{" "}
                          <small style={{ opacity: 0.6 }}>
                            ({t("utilization benchmark")})
                          </small>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="expected_km"
                          value={config.expected_km}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Odometer Start")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="odometer_start"
                          value={config.odometer_start}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Odometer Current")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={config.odometer_start || 0}
                          name="odometer_current"
                          value={config.odometer_current}
                          onChange={changeConfig}
                        />
                      </div>
                    </div>
                    <div
                      className="d-flex flex-wrap"
                      style={{ gap: 12, marginTop: 12 }}
                    >
                      <button
                        className="cx-btn-2"
                        type="submit"
                        disabled={saving}
                      >
                        {saving ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Save Config")
                        )}
                      </button>
                      <button
                        type="button"
                        className="cx-btn-1"
                        onClick={computeTco}
                        disabled={computing}
                      >
                        {computing ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Compute TCO")
                        )}
                      </button>
                      <button
                        type="button"
                        className="cx-btn-1"
                        onClick={computeCpk}
                      >
                        {t("Compute CPK")}
                      </button>
                    </div>
                  </Form>

                  {(tco || cpk) && (
                    <div className="row" style={{ marginTop: 16 }}>
                      {tco && <ResultCard title={t("TCO Result")} data={tco} vehicles={vehicles} />}
                      {cpk && <ResultCard title={t("CPK Result")} data={cpk} vehicles={vehicles} />}
                    </div>
                  )}
                </div>
              </Tab.Pane>

              {/* ===================== TAB 2: Fleet Summary ===================== */}
              <Tab.Pane eventKey="fleet">
                <div>
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap"
                    style={{
                      borderBottom: "0.5px solid #f6efe9",
                      paddingBottom: 12,
                      marginBottom: 20,
                      gap: 12,
                    }}
                  >
                    <div className="vehicleHead">
                      <p
                        style={{
                          borderBottom: "none",
                          paddingBottom: 0,
                          marginBottom: 0,
                        }}
                      >
                        {t("Fleet TCO Summary")}
                      </p>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                      <button className="cx-btn-1" onClick={viewReport}>
                        {t("View Report")}
                      </button>
                      <button
                        className="cx-btn-2"
                        onClick={downloadPdf}
                        disabled={pdfLoading}
                      >
                        {pdfLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Download TCO Report PDF")
                        )}
                      </button>
                    </div>
                  </div>
                  {fleetLoading ? (
                    <Loader />
                  ) : (
                    <div id="scroll_insideThe_Padding_tabel">
                      <div className="VA_table">
                        <table className="table tableAdmin">
                          <thead className="tableHead">
                            <tr>
                              <th>{t("Vehicle")}</th>
                              <th>{t("Acquisition")}</th>
                              <th>{t("Operating")}</th>
                              <th>{t("Depreciation")}</th>
                              <th>{t("Residual")}</th>
                              <th>{t("TCO")}</th>
                              <th>{t("CPK")}</th>
                            </tr>
                          </thead>
                          <tbody className="tableBody">
                            {fleet && fleet.length > 0 ? (
                              fleet.map((r, i) => (
                                <FleetRow r={r} i={i} vehicles={vehicles} key={r.vehicle_id || i} />
                              ))
                            ) : (
                              <tr>
                                <td colSpan={7}>
                                  <NoDataComp />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Tab.Container>
        </div>
      </div>

      {/* TCO JSON Report modal */}
      <Modal
        show={showReport}
        onHide={() => setShowReport(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{report?.title || t("Fleet TCO Report")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportLoading ? (
            <Loader />
          ) : report ? (
            <>
              <div className="row" style={{ marginBottom: 12 }}>
                <div className="col-md-6">
                  <b>{t("Currency")}:</b> {report.currency || "-"}
                </div>
                <div className="col-md-6">
                  <b>{t("Period")}:</b>{" "}
                  {(typeof report.period === "string" && report.period) ||
                    [
                      report.from_date || report.period?.from_date,
                      report.to_date || report.period?.to_date,
                    ]
                      .filter(Boolean)
                      .join(" - ") ||
                    "-"}
                </div>
              </div>
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Acquisition")}</th>
                      <th>{t("Operating")}</th>
                      <th>{t("Depreciation")}</th>
                      <th>{t("Residual")}</th>
                      <th>{t("TCO")}</th>
                      <th>{t("CPK")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {reportRows.length > 0 ? (
                      reportRows.map((r, i) => (
                        <FleetRow r={r} i={i} vehicles={vehicles} key={r.vehicle_id || i} />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {report.totals && (
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td>{t("Grand Total")}</td>
                        <td>{pick(report.totals, ["acquisition_cost", "acquisition"])}</td>
                        <td>{pick(report.totals, ["operating_cost", "operating"])}</td>
                        <td>{pick(report.totals, ["depreciation", "cumulative_depreciation"])}</td>
                        <td>{pick(report.totals, ["residual_value", "residual"])}</td>
                        <td>{pick(report.totals, ["tco", "ownership_cost", "total_cost"])}</td>
                        <td>{pick(report.totals, ["cpk", "cost_per_km"])}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

export default TcoCpk;
